import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import InventoryItem from '../inventory-item';
import { IInventoryItem } from '../inventory-item/types';
import styles from './styles.module.css';
import { IMealItemCategory, ITurnOffBulkMeal } from './types';
import ReasonModal from './modals/turnOffReasonModal';
import { Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as SearchIcon } from '../../header/icons/search.svg';
import { apiService } from '../../../utils/api';
import moment from 'moment';
import { toast } from 'react-toastify';
import { STORAGE_KEYS } from 'utils/constants';

type Props = {
  category: IMealItemCategory;
  handleMealUpdate: any;
  isAddon?: boolean;
  onReloadAddon?: () => void;
  brandName: string;
  workingHours: Array<{
    day: string;
    opening_time: string;
  }>;
};

const baseUrl = process.env.REACT_APP_KITCHEN_API_URL;

const InventoryCategory: FC<Props> = ({
  category,
  handleMealUpdate,
  onReloadAddon,
  isAddon = false,
  brandName,
  workingHours
}) => {
  const [editAll, setEditAll] = useState(false);
  const [unavailableMealsCount, setUnavailableMealsCount] = useState(0);
  const [mealAltered, setMealAltered] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [allMealItems, setAllMealItems] = useState<IInventoryItem[]>([]);
  const [mealItems, setMealItems] = useState<IInventoryItem[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [search, setSearch] = useState('');

  const [saving, setSaving] = useState(false);
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN) as string;

  const attachKeyToMeal = (meal: IInventoryItem) => {
    meal.key = meal.id;
    return meal;
  };

  const attachKeyToMeals = (meals: IInventoryItem[]) => meals?.map((meal) => attachKeyToMeal(meal));

  useEffect(() => {
    setAllMealItems(attachKeyToMeals(category.meals));
  }, [category]);

  useEffect(() => {
    if (allMealItems) {
      setUnavailableMealsCount(allMealItems?.filter((item) => !item.active).length);
      filterMealItems();
    }
  }, [allMealItems]);

  useEffect(() => {
    filterMealItems();
  }, [search]);

  useEffect(() => {
    if (!editAll) {
      setMealAltered(false);
      setAllMealItems(attachKeyToMeals(category.meals));
    }
  }, [editAll]);

  const turnOffAllMealItems = async ({ val, reactivation_time }: ITurnOffBulkMeal) => {
    if (isAddon) {
      // eslint-disable-next-line
      updateAllAddonItems({ val, reactivation_time });
      return;
    }
    setSubmitting(true);
    const comments = val.value === 'Others' ? val.comment : val.value;
    const data = {
      meal_category_id: category.id,
      active: true,
      comment: comments,
      availability_soon_date_: reactivation_time
    };
    await apiService.post('kitchen/supervisor/turn-off-multiple-meals', { data, baseUrl });

    setShowReason(false);
    setSubmitting(false);
  };

  const updateAllAddonItems = async ({ reactivation_time }: ITurnOffBulkMeal) => {
    setSubmitting(true);
    const meals = category?.meals?.map(({ id: meal_id, available_no }) => ({
      meal_id,
      sold_out: Number(available_no) < 1,
      active: false,
      availability_soon_date: reactivation_time,
      cokitchen_id: cokitchenId
    }));
    await apiService.put('kitchen/related-meal', {
      data: { data: meals },
      baseUrl
    });

    setShowReason(false);
    setSubmitting(false);
  };

  const onMealEdited = (meal: IInventoryItem) => {
    setAllMealItems(allMealItems.map((item) => (item.id === meal.id ? meal : item)));
    setMealAltered(true);
  };

  const saveChanges = async () => {
    setSaving(true);

    const meals = allMealItems
      ?.filter((item) => item.altered)
      ?.map(({ id: meal_id, active, available_no }) => ({
        meal_id,
        active,
        sold_out: Number(available_no) < 1,
        availability_soon_date: moment().clone().subtract(1, 'day').format(),
        cokitchen_id: cokitchenId
      }));

    try {
      await apiService.put('kitchen/related-meal', {
        data: { data: meals },
        baseUrl
      });
      toast(<ToastMessage type='success' message='Meal items updated successfully' />);
      if (isAddon && onReloadAddon !== undefined) onReloadAddon();
    } catch {
      toast(<ToastMessage type='error' message='Error updating meal items' />);
    } finally {
      setSaving(false);
      setEditAll(false);
      setMealAltered(false);
    }
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const value = e.target.value;
    setSearch(value.toUpperCase());
    filterMealItems();
  };

  const filterMealItems = () => {
    const filtered = allMealItems?.filter(({ name }) => name?.toUpperCase()?.includes(search));
    setMealItems(attachKeyToMeals(filtered));
  };

  const handleSearchBlur = () => {
    if (search === '') setShowSearch(false);
  };

  return (
    <div className={styles.main}>
      <div className={styles.header}>
        <div className={styles.mainTitle}>
          <div>
            {category.name} {mealAltered ? '*' : ''}
          </div>
          <div>{category?.meals?.length} total items</div>
        </div>
        <div className={styles.mainAction}>
          <button onClick={saveChanges}>{!saving ? 'SAVE CHANGES' : 'SAVING .....'}</button>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.stickyTop}>
          <div>
            <div className={styles.title}>{category.name}</div>
            <div className={styles.detail}>
              {unavailableMealsCount} of {category?.meals?.length} unavailable
            </div>
          </div>
          <div className={styles.actions}>
            <span className='mr-4' role='button' onClick={() => setShowSearch(!showSearch)}>
              <SearchIcon />
            </span>
            <button
              className={`${styles.lineButton} border-b ${
                editAll ? 'text-red-4 border-red-4' : 'text-[#0ea3fd] border-[#0ea3fd]'
              }`}
              onClick={() => setEditAll(!editAll)}>
              {!editAll ? 'Edit all' : 'Cancel'}
            </button>
            <button className={styles.roundedButton} onClick={() => setShowReason(true)}>
              Turn off all
            </button>
          </div>
        </div>
        {showSearch && (
          <div>
            <Input
              autoFocus
              value={search}
              placeholder='Search for meal items'
              type='search'
              small
              onChange={handleSearch}
              onBlur={handleSearchBlur}
            />
          </div>
        )}
        <div>
          {mealItems?.map((mealItem) => (
            <InventoryItem
              key={mealItem.key}
              onMealEdited={onMealEdited}
              inlineEdit={editAll}
              meal={mealItem}
              handleMealUpdate={handleMealUpdate}
              brandName={brandName}
              workingHours={workingHours}
            />
          ))}
          {mealItems?.length === 0 && search !== '' && (
            <p className='mt-[50px] text-center text-xl  text-gray-5'>
              <span>
                <SearchIcon className='mx-auto w-[30px] h-[30px]' />
              </span>
              There are no meal items for <span className='text-gray-6'>{`"${search}"`}</span>. You
              may want to check your spelling
            </p>
          )}
        </div>
      </div>
      <ReasonModal
        show={showReason}
        loading={submitting}
        item={category.name}
        onClose={() => setShowReason(false)}
        onConfirm={turnOffAllMealItems}
        brandName={brandName}
        workingHours={workingHours}
      />
    </div>
  );
};

export default InventoryCategory;
